import { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined';
import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import { AgentFinancials } from '../features/finance/FinancialsTabs';
import { EditRecordButton } from '../components/EditRecordButton';
import { VaultNotes } from '../components/VaultNotes';
import { ProfileHeader } from '../components/ProfileHeader';
import { ViewCommunicationsButton } from '../features/communications/ViewCommunicationsButton';
import { useNotice } from '../components/NoticeProvider';
import { StatTile } from '../components/StatTile';
import { TileGrid } from '../components/TileGrid';
import {
  InfoPanel,
  OutcomesPanel,
  PerformancePanel,
  RateHistoryPanel,
  RecentActivityPanel,
  ServicesPanel,
} from '../features/agents/AgentPanels';
import { useAgentStats } from '../features/agents/stats';
import { useAgents } from '../features/agents/useAgents';
import { ActiveShipmentsTile } from '../features/shipments/ActiveShipmentsTile';
import { ConversationLinksPanel, ShipmentsTabPanel } from '../features/shipments/ShipmentLinkPanels';
import { pct } from '../lib/numbers';

const TABS = ['Overview', 'Rate History', 'Shipments', 'Communications', 'Financials', 'Activity History'] as const;
const GAP = { xs: 1.5, md: 2 };
const FIT = {
  display: 'grid',
  gap: GAP,
  alignItems: 'start',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
};

/** One agent: who they are, how they answer our requests, and every rate they gave */
export function AgentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notice = useNotice();
  const agents = useAgents();
  const statsOf = useAgentStats();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const a = agents.items.find((x) => x.id === id);
  if (agents.loading) return null; // still loading: do not decide "not found" yet
  if (!a) return <Navigate to="/agents" replace />;
  const st = statsOf(a);
  const go = (to: string) => () => void navigate(to);
  const soon = (text: string) => () => notice(text, 'info');

  return (
    <Stack spacing={GAP}>
      <ProfileHeader
        back={{ to: '/agents', label: 'Overseas Agents' }}
        name={a.name}
        status={{ label: `${a.status} Agent`, tone: a.status === 'Active' ? 'success' : 'neutral' }}
        details={[[a.city, a.country].filter(Boolean).join(', '), a.website, a.phone, a.email]}
        tabs={TABS}
        tab={tab}
        onTab={setTab}
        actions={
          <>
            <Button
              variant="contained"
              onClick={soon(
                'Requests for rates go out as approval cards once the server is connected. Nothing was sent.',
              )}
            >
              Request Rate
            </Button>
            <Button variant="outlined" onClick={go('/shipments')}>
              View Shipments
            </Button>
            <ViewCommunicationsButton />
            <EditRecordButton what="agent" />
          </>
        }
      />

      {tab === 'Overview' && (
        <Stack spacing={GAP}>
          <TileGrid lg={3} xl={6}>
            <StatTile
              count={st.total}
              title="Total RFQs"
              note="Sent to this agent"
              icon={<RequestQuoteOutlined />}
              tone="info"
              onClick={() => setTab('Rate History')}
            />
            <StatTile
              count={st.received}
              title={`Rates Received (${st.responseRate}%)`}
              note="Response rate"
              icon={<MarkEmailReadOutlined />}
              tone="success"
              onClick={() => setTab('Rate History')}
            />
            <StatTile
              count={st.won}
              title={`Won Shipments (${pct(st.won, st.total)}%)`}
              note="From quoted opportunities"
              icon={<EmojiEventsOutlined />}
              tone="info"
              onClick={() => setTab('Rate History')}
            />
            <StatTile
              count={st.lost}
              title={`Lost Opportunities (${pct(st.lost, st.total)}%)`}
              note="Not selected"
              icon={<HighlightOffOutlined />}
              tone="error"
              onClick={() => setTab('Rate History')}
            />
            <ActiveShipmentsTile count={st.active} onClick={() => setTab('Shipments')} />
            <StatTile
              count={st.completed}
              title="Completed Shipments"
              note="Successfully delivered"
              icon={<CheckCircleOutlined />}
              tone="success"
              onClick={() => setTab('Shipments')}
            />
          </TileGrid>
          <Box sx={FIT}>
            <InfoPanel a={a} />
            <Stack spacing={GAP}>
              <PerformancePanel st={st} />
              <ServicesPanel a={a} />
            </Stack>
            <OutcomesPanel st={st} />
          </Box>
          <Box sx={FIT}>
            <RateHistoryPanel rfqs={st.rfqs} limit={4} />
            <RecentActivityPanel rfqs={st.rfqs} />
          </Box>
          <VaultNotes notes={a.notes} />
        </Stack>
      )}
      {tab === 'Rate History' && <RateHistoryPanel rfqs={st.rfqs} title="Rate History" />}
      {tab === 'Shipments' && <ShipmentsTabPanel rows={st.ships} />}
      {tab === 'Communications' && <ConversationLinksPanel ships={st.ships} party="Overseas Agent" />}
      {tab === 'Activity History' && <RecentActivityPanel rfqs={st.rfqs} />}
      {tab === 'Financials' && <AgentFinancials agent={a.name} />}
    </Stack>
  );
}
